import { Wrench, Gauge, Zap, Disc, LifeBuoy, Truck, ClipboardCheck, Cog, Thermometer, Droplet, Cpu, Rocket, type LucideIcon } from "lucide-react";
import type { Faq, PhotoName } from "@/lib/site-data";
import { SERVICE_OPTIONS, type ServiceOption } from "@/lib/service-options";

export type Service = {
  slug: string;
  name: string;
  icon: LucideIcon;
  /** One-line card description. */
  short: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  /** Noun used in "Signs You Need …" and the CTA. */
  noun: string;
  included: string[];
  signs: string[];
  onsite: { t: string; d: string }[];
  note?: string;
  faqs: Faq[];
  review: "mccleary" | "cindy" | "deb";
  related: string[];
  photo: PhotoName;
  option: ServiceOption;
};

const BRAND = "406 Mobile Mechanic";

export const SERVICES: Service[] = [
  {
    slug: "mobile-auto-repair",
    name: "Mobile Auto Repair",
    icon: Wrench,
    short: "The mechanic that comes to you. Full-service auto repair in your driveway, parking lot, or job site anywhere in Great Falls — so you never have to arrange a tow or a ride.",
    h1: "Mobile Auto Repair in Great Falls, MT",
    title: `Mobile Auto Repair Great Falls MT | ${BRAND}`,
    description: "Full-service mobile auto repair in Great Falls, MT — at your home, job site or roadside. Open 7 days, 8AM–9PM. Call or text 406-799-0095.",
    intro: "Skip the tow and the waiting room. Josh brings a fully equipped work truck to your driveway, parking lot or job site and handles the repair right there.",
    noun: "Mobile Auto Repair",
    included: [
      "On-site diagnosis of noises, leaks, warning lights and drivability problems",
      "Replacement of worn or failed parts — starters, alternators, belts, hoses, sensors and more",
      "Brake, suspension, cooling system and electrical repairs",
      "Parts sourced for your exact vehicle before the visit",
      "A walk-through of what failed and what was replaced",
    ],
    signs: [
      "Your vehicle won't start or stalls out",
      "A new noise, smell, vibration or leak",
      "A warning light on the dash",
      "You don't have time to drop the car at a shop",
      "The vehicle isn't safe to drive to a shop",
    ],
    onsite: [
      { t: "No tow, no ride needed", d: "The repair happens where the vehicle already is." },
      { t: "Keep your day moving", d: "Work at home or on the job while the vehicle gets fixed." },
      { t: "See it for yourself", d: "Josh shows you the problem firsthand before and after the repair." },
    ],
    faqs: [
      { q: "What repairs can be done in my driveway?", a: "Most common repairs — starters, alternators, brakes, belts, sensors, cooling and electrical work — can be done on site. Call or text with your vehicle and the problem and Josh will tell you straight what's possible." },
      { q: "Do I need to be home during the repair?", a: "Not always. As long as Josh can get to the vehicle and the keys, many jobs can be done while you're at work. Talk it through when you book." },
      { q: "How fast can you get to me?", a: "It depends on the day's schedule and where you are. Call or text and you'll get a straight answer on timing." },
    ],
    review: "cindy",
    related: ["diagnostics-check-engine-light", "brakes-suspension", "roadside-assistance"],
    photo: "hero",
    option: "Mobile Auto Repair",
  },
  {
    slug: "diagnostics-check-engine-light",
    name: "Diagnostics & Check Engine Light",
    icon: Gauge,
    short: "Check engine light diagnostics with pro scan tools — find the real cause, not a guess.",
    h1: "Check Engine Light Diagnostics in Great Falls, MT",
    title: `Check Engine Light Diagnostics Great Falls MT | ${BRAND}`,
    description: "Mobile check engine light and drivability diagnostics in Great Falls, MT. The real cause found on site, not a guess. Call or text 406-799-0095.",
    intro: "A code is a starting point, not an answer. Josh scans, tests and traces the fault on site so you only pay to fix what's actually wrong.",
    noun: "Diagnostics",
    included: [
      "Scan tool reading of stored and pending trouble codes",
      "Live data review of sensors, fuel trims and engine performance",
      "Hands-on testing to confirm the failed part before anything is replaced",
      "A clear explanation of the cause and your repair options",
      "A labor quote for the fix",
    ],
    signs: [
      "Check engine light is on or flashing",
      "Rough idle, misfires or hesitation",
      "Worse fuel mileage than usual",
      "Failed emissions or a strange exhaust smell",
      "The vehicle runs different but there's no obvious cause",
    ],
    onsite: [
      { t: "Test it where it fails", d: "Some problems only show up in your conditions — cold mornings, your commute." },
      { t: "No parts cannon", d: "The failed part is confirmed before money is spent on replacements." },
      { t: "Fix it on the same visit", d: "When the parts are on hand, the repair can often happen right after the diagnosis." },
    ],
    faqs: [
      { q: "Can I keep driving with the check engine light on?", a: "A steady light usually means get it checked soon. A flashing light often means an active misfire that can damage the catalytic converter — have it looked at before driving much further." },
      { q: "Isn't a code reader enough?", a: "A code tells you which system flagged a problem, not which part failed. Proper testing finds the actual cause so you don't replace parts that weren't broken." },
      { q: "Will you show me what you found?", a: "Yes. Josh shows you the problem firsthand and explains the options before any repair." },
    ],
    review: "cindy",
    related: ["electrical-diagnostics", "oil-changes-tune-ups", "pre-purchase-inspections"],
    photo: "truck",
    option: "Diagnostics",
  },
  {
    slug: "electrical-diagnostics",
    name: "Electrical Diagnostics",
    icon: Zap,
    short: "Auto electrical repair for no-starts, wiring faults, batteries and charging systems.",
    h1: "Auto Electrical Diagnostics in Great Falls, MT",
    title: `Auto Electrical Repair Great Falls MT | ${BRAND}`,
    description: "Mobile auto electrical diagnostics in Great Falls, MT — no-starts, wiring faults, batteries and charging systems traced on site. Call or text 406-799-0095.",
    intro: "Electrical problems take patience. Josh works through the system methodically — even on complex or modified vehicles — until the fault is found and fixed.",
    noun: "Electrical Diagnostics",
    included: [
      "Battery, starter and charging system testing",
      "Tracing shorts, open circuits and parasitic battery drains",
      "Wiring and connector repair, including hard-to-reach harnesses",
      "Lighting, power accessory and fuse/relay troubleshooting",
      "Diagnosis on modified vehicles and multi-battery setups",
    ],
    signs: [
      "No-start or a click when you turn the key",
      "Battery keeps dying overnight",
      "Dim or flickering lights",
      "Accessories or gauges that work some of the time",
      "Blown fuses that keep coming back",
    ],
    onsite: [
      { t: "Diagnose a no-start where it sits", d: "A car that won't start can't drive to a shop anyway." },
      { t: "Methodical, not rushed", d: "Josh traces the circuit step by step and shows you what he found." },
      { t: "Complex vehicles welcome", d: "Conversions, multiple battery systems and tricky wiring are part of the job." },
    ],
    faqs: [
      { q: "Why does my battery keep dying?", a: "It could be the battery itself, a charging problem, or something drawing power with the key off. Testing each one on site separates the three." },
      { q: "Can you work on modified or older vehicles?", a: "Yes. Josh has traced electrical faults on complex conversions with multiple battery systems and hard-to-reach wiring." },
      { q: "Do electrical problems take long to find?", a: "Some are quick, some take careful tracing. You'll know what's happening throughout — communication is key." },
    ],
    review: "mccleary",
    related: ["diagnostics-check-engine-light", "roadside-assistance", "performance-parts-install"],
    photo: "lettering",
    option: "Electrical",
  },
  {
    slug: "brakes-suspension",
    name: "Brakes & Suspension",
    icon: Disc,
    short: "Brake repair, rotors, shocks and steering parts replaced in your driveway.",
    h1: "Mobile Brake Repair in Great Falls, MT",
    title: `Mobile Brake Repair Great Falls MT | ${BRAND}`,
    description: "Mobile brake and suspension repair in Great Falls, MT — pads, rotors, calipers, shocks and steering parts done in your driveway. Call or text 406-799-0095.",
    intro: "Brakes and suspension are a safety issue, not something to put off. Josh replaces worn parts in your driveway so you're not driving a problem across town.",
    noun: "Brake & Suspension Repair",
    included: [
      "Brake pad and rotor replacement",
      "Caliper, brake hose and brake line repair",
      "Brake fluid service and bleeding",
      "Shocks, struts and suspension component replacement",
      "Steering parts — tie rods, ball joints and related components",
    ],
    signs: [
      "Grinding or squealing when you brake",
      "A soft or spongy brake pedal",
      "The vehicle pulls to one side",
      "Clunks over bumps or excessive bouncing",
      "Loose or wandering steering",
    ],
    onsite: [
      { t: "Don't drive it worn out", d: "Bad brakes shouldn't be driven to a shop — the shop comes to you." },
      { t: "See the worn parts", d: "Josh shows you the old pads, rotors or parts so you know why they were replaced." },
    ],
    faqs: [
      { q: "Can brakes really be done in a driveway?", a: "Yes. Pads, rotors, calipers and most brake work can be done on a flat, safe surface like a driveway or parking lot." },
      { q: "What does grinding mean?", a: "Grinding usually means the pads are worn through and metal is contacting the rotor. Get it checked soon to avoid more damage." },
      { q: "Do you do suspension and steering too?", a: "Yes — shocks, struts, tie rods, ball joints and related parts." },
    ],
    review: "cindy",
    related: ["diagnostics-check-engine-light", "oil-changes-tune-ups", "pre-purchase-inspections"],
    photo: "hero",
    option: "Brakes & Suspension",
  },
  {
    slug: "roadside-assistance",
    name: "Roadside Assistance",
    icon: LifeBuoy,
    short: "Roadside assistance in Great Falls for jump-starts, breakdowns and emergencies.",
    h1: "Roadside Assistance in Great Falls, MT",
    title: `Roadside Assistance Great Falls MT | ${BRAND}`,
    description: "Roadside help in Great Falls, MT — jump-starts, breakdowns and cold-weather no-starts during business hours, 7 days a week. Call or text 406-799-0095.",
    intro: "Broken down on the side of the road or stuck in a parking lot? Call or text and Josh will get to you as quickly as possible during business hours, 7 days a week.",
    noun: "Roadside Assistance",
    included: [
      "Jump-starts and battery testing",
      "Cold-weather no-start diagnosis",
      "On-the-spot repairs for breakdowns when possible",
      "Belt, hose and minor part replacement at the roadside",
      "Towing and transportation when on-site repair isn't possible",
    ],
    signs: [
      "Your vehicle won't start in the cold",
      "You've broken down on the road or at work",
      "A dead battery and no one to jump it",
      "Overheating or a sudden loss of power",
      "A warning light that made you pull over",
    ],
    onsite: [
      { t: "Emergency help during business hours", d: "Open 7 days, 8 AM–9 PM. Call or text and you'll get a straight answer on timing." },
      { t: "Repair first, tow if needed", d: "If it can be fixed on site, it will be. If not, towing and transportation are available." },
      { t: "Out-of-town calls", d: "Josh has traveled out to Dutton last minute for an emergency." },
    ],
    faqs: [
      { q: "Do you offer 24-hour roadside service?", a: "No — emergency help is available during business hours, 8 AM to 9 PM, 7 days a week." },
      { q: "What if my car can't be fixed on the spot?", a: "Towing and transportation are available for vehicles that can't be repaired on site." },
      { q: "Do you come out of town?", a: "Yes. We serve Great Falls and surrounding towns, with statewide travel available. Call or text with your location." },
    ],
    review: "deb",
    related: ["towing-transportation", "electrical-diagnostics", "mobile-auto-repair"],
    photo: "truck",
    option: "Roadside Assistance",
  },
  {
    slug: "towing-transportation",
    name: "Towing & Transportation",
    icon: Truck,
    short: "Towing in Great Falls, MT when a vehicle can't be repaired on site.",
    h1: "Towing & Vehicle Transportation in Great Falls, MT",
    title: `Towing Great Falls MT | ${BRAND}`,
    description: "Towing and vehicle transportation in Great Falls, MT for vehicles that can't be repaired on site. Open 7 days, 8AM–9PM. Call or text 406-799-0095.",
    intro: "Most jobs get fixed where the vehicle sits. When one can't, Josh loads it on the trailer and moves it where it needs to go.",
    noun: "Towing",
    included: [
      "Trailer transport for vehicles that can't be repaired on site",
      "Moving non-running vehicles between locations",
      "Careful loading and strapping",
      "Coordination with an on-site repair when possible",
    ],
    signs: [
      "The vehicle can't be safely repaired where it is",
      "A non-running vehicle needs to be moved",
      "A larger repair needs a different location",
      "You bought a vehicle that doesn't run",
    ],
    onsite: [
      { t: "One call for repair and tow", d: "The same person who diagnoses the problem can move the vehicle if needed." },
      { t: "Repair before tow", d: "If it can be fixed on site, you save the tow altogether." },
      { t: "Handled with care", d: "Vehicles are loaded and strapped down properly on the trailer." },
    ],
    faqs: [
      { q: "Do I need a tow, or can it be repaired where it is?", a: "Call or text with what happened. Many breakdowns can be repaired on site through mobile auto repair or roadside assistance, which saves you the tow." },
      { q: "Can you move a vehicle that doesn't run?", a: "Yes, non-running vehicles can be transported on the trailer." },
      { q: "How far will you tow?", a: "Call or text with the pickup and drop-off locations and Josh will tell you what's possible." },
    ],
    review: "deb",
    related: ["roadside-assistance", "mobile-auto-repair", "engine-replacement"],
    photo: "towing",
    option: "Towing & Transportation",
  },
  {
    slug: "pre-purchase-inspections",
    name: "Pre-Purchase Inspections",
    icon: ClipboardCheck,
    short: "Pre-purchase car inspection in Great Falls before you hand over the cash.",
    h1: "Pre-Purchase Car Inspections in Great Falls, MT",
    title: `Pre-Purchase Car Inspection Great Falls MT | ${BRAND}`,
    description: "Mobile pre-purchase vehicle inspections in Great Falls, MT. We meet you where the vehicle is so you can negotiate or walk away. Call or text 406-799-0095.",
    intro: "Find the problems before your money changes hands. Josh meets you where the vehicle is located and gives you a straight read on its condition.",
    noun: "a Pre-Purchase Inspection",
    included: [
      "Visual inspection of engine, transmission, brakes, suspension and steering",
      "Scan for stored trouble codes",
      "Check for leaks, worn parts and signs of past damage",
      "Test drive observations when the seller allows",
      "A plain-language rundown of what you found and what it means",
    ],
    signs: [
      "You're buying a used car or truck from a private seller",
      "The listing looks too good to be true",
      "You're buying out of town and can't judge it yourself",
      "You want leverage to negotiate the price",
    ],
    onsite: [
      { t: "We meet you at the vehicle", d: "At the seller's driveway, the lot, or wherever the vehicle is parked." },
      { t: "Negotiate or walk away", d: "Know what needs work so you can adjust the offer or pass on it." },
      { t: "Straight talk", d: "An honest opinion, not a sales pitch." },
    ],
    faqs: [
      { q: "Can you inspect a vehicle at the seller's location?", a: "Yes. We meet you where the vehicle is located." },
      { q: "What will an inspection tell me?", a: "What's worn, what's leaking, what codes are stored and what repairs may be coming, so you can negotiate or walk away." },
      { q: "How do I schedule one?", a: "Call or text 406-799-0095 with the vehicle and its location, or send a request through the form." },
    ],
    review: "cindy",
    related: ["diagnostics-check-engine-light", "brakes-suspension", "transmission-services"],
    photo: "lettering",
    option: "Pre-Purchase Inspection",
  },
  {
    slug: "transmission-services",
    name: "Transmission Services",
    icon: Cog,
    short: "Transmission diagnosis, fluid service and repair without the shop wait.",
    h1: "Mobile Transmission Service in Great Falls, MT",
    title: `Transmission Service Great Falls MT | ${BRAND}`,
    description: "Mobile transmission diagnosis, fluid service and repair in Great Falls, MT — larger jobs often handled on site. Call or text 406-799-0095.",
    intro: "Shifting problems don't always mean a new transmission. Josh diagnoses on site, handles fluid service and repairs, and talks you through larger jobs.",
    noun: "Transmission Service",
    included: [
      "Transmission diagnosis and code scanning",
      "Fluid and filter service",
      "Leak diagnosis and seal repair",
      "Sensor, solenoid and linkage repair",
      "Transmission replacement — often handled on site",
    ],
    signs: [
      "Slipping, hard or delayed shifts",
      "Leaking red or brown fluid",
      "A transmission warning light or limp mode",
      "Whining, clunking or shuddering",
      "Burning smell after driving",
    ],
    onsite: [
      { t: "Diagnose before you decide", d: "Find out whether it's fluid, a sensor or something bigger before committing." },
      { t: "Larger jobs on site", d: "Even big transmission work can often be done where the vehicle is — call to discuss." },
      { t: "No shop wait", d: "Skip weeks in a shop queue." },
    ],
    note: "Larger jobs like transmission replacement can often be handled on site — call to discuss.",
    faqs: [
      { q: "Can a transmission be replaced on site?", a: "Often, yes. It depends on the vehicle and the location — call or text to talk it through." },
      { q: "Does slipping mean I need a new transmission?", a: "Not always. Low fluid, a sensor or a solenoid can cause similar symptoms. Diagnosis comes first." },
      { q: "How often should transmission fluid be serviced?", a: "It varies by vehicle and how it's used. Josh can check the fluid condition and recommend what makes sense." },
    ],
    review: "cindy",
    related: ["engine-replacement", "diagnostics-check-engine-light", "towing-transportation"],
    photo: "truck",
    option: "Transmission",
  },
  {
    slug: "heating-ac-repair",
    name: "Heating & AC Repair",
    icon: Thermometer,
    short: "Heater and AC repair for Montana winters and summer heat.",
    h1: "Car Heating & AC Repair in Great Falls, MT",
    title: `Car AC & Heater Repair Great Falls MT | ${BRAND}`,
    description: "Mobile car heater and AC repair in Great Falls, MT — weak airflow, no heat, warm AC. Verified cold before we call it done. Call or text 406-799-0095.",
    intro: "Montana doesn't do mild. Josh diagnoses and repairs heating and AC systems on site, and checks vent temperature before calling the job done.",
    noun: "Heating & AC Repair",
    included: [
      "AC performance testing and vent temperature checks",
      "AC leak diagnosis and component replacement",
      "Heater core, thermostat and coolant system diagnosis",
      "Blower motor, resistor and blend door repair",
      "Climate control electrical troubleshooting",
    ],
    signs: [
      "AC blows warm or only slightly cool",
      "Weak airflow from the vents",
      "No heat or slow warm-up in winter",
      "Windows won't defrost",
      "Strange smells or noises when the fan runs",
    ],
    onsite: [
      { t: "Verified before done", d: "Vent temperature is checked so you know the system works." },
      { t: "Stay comfortable", d: "No sitting in a cold waiting room in January." },
      { t: "Clear explanation", d: "Josh shows you what failed and why." },
    ],
    faqs: [
      { q: "Why is my AC blowing warm?", a: "Common causes include low refrigerant from a leak, a failed compressor or clutch, or an electrical fault. Testing finds which one." },
      { q: "Why won't my heater get hot?", a: "Low coolant, a stuck thermostat, a clogged heater core or a blend door problem are common causes." },
      { q: "Can AC repairs be done in my driveway?", a: "Many can. Call or text with the symptoms and Josh will let you know." },
    ],
    review: "cindy",
    related: ["electrical-diagnostics", "diagnostics-check-engine-light", "oil-changes-tune-ups"],
    photo: "ac",
    option: "Heating & AC",
  },
  {
    slug: "oil-changes-tune-ups",
    name: "Oil Changes & Tune-Ups",
    icon: Droplet,
    short: "Mobile oil changes and tune-ups at home or at work.",
    h1: "Mobile Oil Changes & Tune-Ups in Great Falls, MT",
    title: `Mobile Oil Change Great Falls MT | ${BRAND}`,
    description: "Mobile oil changes and tune-ups in Great Falls, MT at your home or workplace. Open 7 days, 8AM–9PM. Call or text 406-799-0095.",
    intro: "Routine maintenance without the trip. Josh does oil changes and tune-ups at your home or workplace so you never lose an afternoon to it.",
    noun: "an Oil Change or Tune-Up",
    included: [
      "Engine oil and filter change",
      "Spark plugs and ignition parts",
      "Air and cabin filter replacement",
      "Fluid level checks and top-offs",
      "A look at belts, hoses, brakes and tires while we're there",
    ],
    signs: [
      "You're past your oil change interval",
      "Oil change reminder on the dash",
      "Rough idle or reduced power",
      "Worse fuel economy",
      "Hard starts",
    ],
    onsite: [
      { t: "Done while you work", d: "Maintenance happens in the parking lot or driveway." },
      { t: "Catch problems early", d: "A quick look around the vehicle can spot issues before they leave you stranded." },
      { t: "Fits fleets too", d: "Several vehicles can be serviced in one visit." },
    ],
    faqs: [
      { q: "Is a mobile oil change messy?", a: "No. Used oil and filters are collected and taken away." },
      { q: "Can you service several vehicles at once?", a: "Yes — that's common for families and fleets. See fleet service for business vehicles." },
      { q: "What's included in a tune-up?", a: "It depends on the vehicle, but typically spark plugs, filters and an ignition and fluid check. Josh will quote it for your vehicle." },
    ],
    review: "cindy",
    related: ["brakes-suspension", "diagnostics-check-engine-light", "heating-ac-repair"],
    photo: "truck",
    option: "Oil Change/Tune-Up",
  },
  {
    slug: "engine-replacement",
    name: "Engine Replacement",
    icon: Cpu,
    short: "Engine replacements handled start to finish by an experienced mechanic.",
    h1: "Engine Replacement in Great Falls, MT",
    title: `Engine Replacement Great Falls MT | ${BRAND}`,
    description: "Engine replacement in Great Falls, MT handled start to finish — larger jobs often done on site. Call or text 406-799-0095 to discuss.",
    intro: "When an engine is done, you need a straight answer and a clean job. Josh handles engine replacements start to finish — often right where the vehicle is.",
    noun: "Engine Replacement",
    included: [
      "Diagnosis to confirm the engine needs replacing",
      "Sourcing a replacement engine for your vehicle",
      "Removal and installation",
      "Transfer of accessories, sensors and wiring",
      "Startup checks and a walk-through of the finished job",
    ],
    signs: [
      "Loud knocking from the engine",
      "Major oil consumption or heavy smoke",
      "Low compression or a failed engine test",
      "Overheating damage",
      "The engine has seized",
    ],
    onsite: [
      { t: "Larger jobs on site", d: "Engine replacements can often be done where the vehicle sits — call to discuss." },
      { t: "Confirm before committing", d: "Diagnosis comes first so you don't replace an engine that could be repaired." },
      { t: "Clear quote", d: "Parts are sourced and the labor quote sent before the work starts." },
    ],
    note: "Larger jobs like engine replacement can often be handled on site — call to discuss.",
    faqs: [
      { q: "Can an engine really be swapped on site?", a: "Often, yes, depending on the vehicle and the location. Call or text to discuss your situation." },
      { q: "Is replacement worth it on my vehicle?", a: "Josh will give you a straight opinion based on the vehicle's condition and the quote." },
      { q: "Who sources the engine?", a: "Josh can source the replacement engine and parts and include them in the quote." },
    ],
    review: "cindy",
    related: ["transmission-services", "diagnostics-check-engine-light", "towing-transportation"],
    photo: "hero",
    option: "Engine Replacement",
  },
  {
    slug: "performance-parts-install",
    name: "Performance Parts Install",
    icon: Rocket,
    short: "Clean, careful installation of performance and aftermarket parts.",
    h1: "Performance Parts Installation in Great Falls, MT",
    title: `Performance Parts Install Great Falls MT | ${BRAND}`,
    description: "Clean, careful installation of performance and aftermarket parts in Great Falls, MT — done at your place. Call or text 406-799-0095.",
    intro: "Bought the parts? Josh installs performance and aftermarket upgrades cleanly and carefully, right in your driveway or garage.",
    noun: "Performance Parts Installation",
    included: [
      "Installation of customer-supplied or sourced aftermarket parts",
      "Intake, exhaust and suspension upgrades",
      "Lighting and electrical accessory installs",
      "Clean wiring and secure mounting",
      "A check that everything works before the job is done",
    ],
    signs: [
      "You've bought upgrades and need them installed",
      "A previous install was done poorly",
      "You want accessories wired in cleanly",
      "You're building up a truck or project car",
    ],
    onsite: [
      { t: "In your garage", d: "No hauling a project vehicle across town." },
      { t: "Clean work", d: "Careful installs and tidy wiring." },
      { t: "Talk it through", d: "Josh will tell you straight what fits and what to watch for." },
    ],
    faqs: [
      { q: "Can I supply my own parts?", a: "Yes. Tell Josh what you have when you call or text so he can plan the install." },
      { q: "Do you wire in lighting and accessories?", a: "Yes — lighting, accessories and other electrical add-ons." },
      { q: "Will you work on modified vehicles?", a: "Yes, including conversions and builds with non-stock wiring." },
    ],
    review: "mccleary",
    related: ["electrical-diagnostics", "brakes-suspension", "mobile-auto-repair"],
    photo: "lettering",
    option: "Performance Parts",
  },
];

export function getService(slug: string) {
  return SERVICES.find((s) => s.slug === slug);
}

/** Accepts a dropdown label ("Brakes & Suspension") or a service page slug ("brakes-suspension"). */
export function resolveServiceParam(v: unknown): ServiceOption | "" {
  if (typeof v !== "string") return "";
  if ((SERVICE_OPTIONS as readonly string[]).includes(v)) return v as ServiceOption;
  return getService(v)?.option ?? "";
}

